import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Pencil, Plus } from "lucide-react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import type { Label, LabelColor } from "@kandoo/shared";
import { api, type BoardDetail, type CardDetail } from "../api/client";
import { LabelChip } from "./LabelChip";
import { ConfirmButton } from "./ConfirmButton";
import { LABEL_COLOR_KEYS, labelColor } from "../util/labelColors";

/**
 * Write a card's label set into both caches that render it — the card modal's
 * ["card", id] and the board's ["board", id] — so a toggle shows instantly
 * instead of after a round trip and two refetches.
 */
function setCardLabels(
  qc: QueryClient,
  boardId: string,
  cardId: string,
  update: (labels: Label[]) => Label[],
) {
  qc.setQueryData<CardDetail>(["card", cardId], (c) =>
    c ? { ...c, labels: update(c.labels) } : c,
  );
  qc.setQueryData<BoardDetail>(["board", boardId], (b) =>
    b
      ? {
          ...b,
          lists: b.lists.map((l) => ({
            ...l,
            cards: l.cards.map((c) =>
              c.id === cardId ? { ...c, labels: update(c.labels) } : c,
            ),
          })),
        }
      : b,
  );
}

/** Toggle / create-and-apply labels on one card, optimistically. */
export function useCardLabelMutations(boardId: string, cardId: string) {
  const qc = useQueryClient();
  const settle = () => {
    qc.invalidateQueries({ queryKey: ["card", cardId] });
    qc.invalidateQueries({ queryKey: ["board", boardId] });
  };
  const sortByPosition = (ls: Label[]) =>
    [...ls].sort((a, b) => a.position - b.position);

  const toggle = useMutation({
    mutationFn: ({ label, on }: { label: Label; on: boolean }) =>
      on
        ? api.labels.assign(cardId, label.id)
        : api.labels.unassign(cardId, label.id),
    onMutate: async ({ label, on }) => {
      // a poll landing mid-toggle would briefly undo the optimistic state
      await qc.cancelQueries({ queryKey: ["card", cardId] });
      await qc.cancelQueries({ queryKey: ["board", boardId] });
      setCardLabels(qc, boardId, cardId, (ls) =>
        on
          ? ls.some((l) => l.id === label.id)
            ? ls
            : sortByPosition([...ls, label])
          : ls.filter((l) => l.id !== label.id),
      );
    },
    onSettled: settle,
  });

  const createAndApply = useMutation({
    mutationFn: async (b: { color: LabelColor; name: string }) => {
      const label = await api.labels.create(boardId, b);
      await api.labels.assign(cardId, label.id);
      return label;
    },
    onSettled: settle,
  });

  return { toggle, createAndApply };
}

/** The first palette color the board isn't using yet (cycles once all are). */
function nextColor(palette: Label[]): LabelColor {
  const used = new Set(palette.map((l) => l.color));
  return (
    LABEL_COLOR_KEYS.find((c) => !used.has(c)) ??
    LABEL_COLOR_KEYS[palette.length % LABEL_COLOR_KEYS.length]
  );
}

const labelText = (l: Label) => (l.name ?? labelColor(l.color).label).toLowerCase();

interface LabelPickerProps {
  boardId: string;
  cardId: string;
  /** Labels currently on the card. */
  assigned: Label[];
  onClose: () => void;
}

/**
 * Type-to-filter label picker. Arrows move, Enter toggles the highlighted
 * label, and when the text names no existing label the last row offers to
 * create it — which also applies it to the card. Each row's pencil edits the
 * board-wide label (rename, recolor, delete) in place.
 */
export function LabelPicker({ boardId, cardId, assigned, onClose }: LabelPickerProps) {
  const qc = useQueryClient();
  // shares the board page's cache (and its poll), so the palette stays live
  const { data: board } = useQuery({
    queryKey: ["board", boardId],
    queryFn: () => api.boards.get(boardId),
  });
  const palette = useMemo(
    () => [...(board?.labels ?? [])].sort((a, b) => a.position - b.position),
    [board],
  );
  // how many cards on the board's face carry each label (shown before delete)
  const usage = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of board?.lists ?? [])
      for (const c of l.cards)
        for (const lb of c.labels) m.set(lb.id, (m.get(lb.id) ?? 0) + 1);
    return m;
  }, [board]);

  const { toggle, createAndApply } = useCardLabelMutations(boardId, cardId);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["card", cardId] });
    qc.invalidateQueries({ queryKey: ["board", boardId] });
  };
  const updateLabel = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { color?: LabelColor; name?: string | null } }) =>
      api.labels.update(id, patch),
    onSuccess: invalidate,
  });
  const deleteLabel = useMutation({
    mutationFn: (id: string) => api.labels.delete(id),
    onSuccess: invalidate,
  });

  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const assignedIds = new Set(assigned.map((l) => l.id));
  const q = query.trim().toLowerCase();
  const matches = q ? palette.filter((l) => labelText(l).includes(q)) : palette;
  const canCreate = !!q && !palette.some((l) => (l.name ?? "").toLowerCase() === q);
  const rowCount = matches.length + (canCreate ? 1 : 0);
  // keep the highlight on a real row as the filter narrows
  const activeRow = Math.min(active, Math.max(rowCount - 1, 0));

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  function activate(row: number) {
    if (row < matches.length) {
      const label = matches[row];
      toggle.mutate({ label, on: !assignedIds.has(label.id) });
    } else if (canCreate) {
      createAndApply.mutate({ color: nextColor(palette), name: query.trim() });
      setQuery("");
      setActive(0);
    }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((activeRow + 1) % Math.max(rowCount, 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((activeRow - 1 + rowCount) % Math.max(rowCount, 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      activate(activeRow);
    } else if (e.key === "Escape") {
      // close the picker only — not the card modal around it
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  }

  return (
    <div
      className="w-72 rounded-lg border border-slate-200 bg-[var(--surface)] p-2 shadow-lg"
      role="dialog"
      aria-label="Labels"
      onKeyDown={(e) => {
        // Escape from anywhere inside (e.g. a row's rename field) closes it
        if (e.key === "Escape" && e.target !== inputRef.current) {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <input
        ref={inputRef}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
        }}
        onKeyDown={onKeyDown}
        placeholder="Search or create a label…"
        aria-label="Search or create a label"
        maxLength={50}
        className="w-full rounded border border-slate-300 bg-[var(--surface)] px-2 py-1 text-sm outline-none focus:border-blue-400"
      />
      <ul className="themed-scroll mt-2 max-h-72 overflow-y-auto" role="listbox">
        {matches.map((l, i) =>
          editingId === l.id ? (
            <LabelEditor
              key={l.id}
              label={l}
              usage={usage.get(l.id) ?? 0}
              onRename={(name) => updateLabel.mutate({ id: l.id, patch: { name: name || null } })}
              onRecolor={(color) => updateLabel.mutate({ id: l.id, patch: { color } })}
              onDelete={() => {
                deleteLabel.mutate(l.id);
                setEditingId(null);
              }}
              onDone={() => {
                setEditingId(null);
                inputRef.current?.focus();
              }}
            />
          ) : (
            <li
              key={l.id}
              role="option"
              aria-selected={assignedIds.has(l.id)}
              onMouseEnter={() => setActive(i)}
              className={`flex items-center gap-2 rounded px-1.5 py-1 ${
                i === activeRow ? "bg-slate-100" : ""
              }`}
            >
              <button
                type="button"
                onClick={() => activate(i)}
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center text-blue-600">
                  {assignedIds.has(l.id) && <Check size={14} />}
                </span>
                <LabelChip label={l} />
              </button>
              <button
                type="button"
                onClick={() => setEditingId(l.id)}
                title="Edit label"
                aria-label={`Edit label ${l.name ?? l.color}`}
                className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
              >
                <Pencil size={12} />
              </button>
            </li>
          ),
        )}
        {canCreate && (
          <li
            onMouseEnter={() => setActive(matches.length)}
            className={`rounded px-1.5 py-1 ${
              activeRow === matches.length ? "bg-slate-100" : ""
            }`}
          >
            <button
              type="button"
              onClick={() => activate(matches.length)}
              className="flex w-full items-center gap-2 text-left text-sm text-slate-700"
            >
              <Plus size={14} className="shrink-0 text-slate-400" />
              Create
              <LabelChip label={{ ...placeholderLabel, color: nextColor(palette), name: query.trim() }} />
            </button>
          </li>
        )}
        {rowCount === 0 && (
          <li className="px-1.5 py-1 text-sm text-slate-500">
            Type a name to create the first label.
          </li>
        )}
      </ul>
      <p className="mt-2 border-t border-slate-100 px-1 pt-1.5 text-[11px] text-slate-400">
        ↑↓ move · Enter toggle · on the board: hover a card, press L
      </p>
    </div>
  );
}

/** Stand-in for previewing a not-yet-created label in the Create row. */
const placeholderLabel: Label = {
  id: "new",
  boardId: "new",
  name: null,
  color: "gray",
  position: 0,
  createdAt: new Date(0).toISOString(),
  updatedAt: new Date(0).toISOString(),
};

function LabelEditor({
  label,
  usage,
  onRename,
  onRecolor,
  onDelete,
  onDone,
}: {
  label: Label;
  usage: number;
  onRename: (name: string) => void;
  onRecolor: (c: LabelColor) => void;
  onDelete: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(label.name ?? "");
  const commitName = () => {
    if (name.trim() !== (label.name ?? "")) onRename(name.trim());
  };
  return (
    <li className="space-y-2 rounded border border-slate-200 p-2">
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={commitName}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitName();
            onDone();
          }
        }}
        maxLength={50}
        placeholder="Label name (optional)"
        aria-label="Label name"
        className="w-full rounded border border-slate-300 bg-[var(--surface)] px-2 py-1 text-sm outline-none focus:border-blue-400"
      />
      <div className="flex flex-wrap gap-1">
        {LABEL_COLOR_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            title={labelColor(key).label}
            aria-label={labelColor(key).label}
            onClick={() => onRecolor(key)}
            className={`h-6 w-6 rounded ${labelColor(key).swatch} ${
              label.color === key ? "ring-2 ring-slate-900 ring-offset-1" : ""
            }`}
          />
        ))}
      </div>
      <div className="flex items-center justify-between text-xs">
        <ConfirmButton
          onConfirm={onDelete}
          title="Delete this label from the whole board"
          className="text-slate-400 hover:text-red-600"
          confirmClassName="rounded bg-red-600 px-1.5 py-0.5 text-white"
          confirmLabel={
            usage > 0
              ? `Remove from ${usage} ${usage === 1 ? "card" : "cards"} and delete?`
              : "Delete label?"
          }
        >
          Delete label
        </ConfirmButton>
        <button
          type="button"
          onClick={() => {
            commitName();
            onDone();
          }}
          className="rounded bg-slate-100 px-2 py-0.5 text-slate-700 hover:bg-slate-200"
        >
          Done
        </button>
      </div>
    </li>
  );
}
